import { Link } from 'react-router-dom';
import { CrossFrameworkMapping } from "../../../components/cross-framework-mapping";
import { GenericCatalogTable } from "../../../components/generic-catalog-table";
import { ScrollReveal } from "../../../components/scroll-reveal";
import {
  ISO27001_CATALOG,
  ISO_ANNEX_A_CATEGORIES,
  ISO_CLAUSES,
  ISO_TO_INDIA_CROSSMAP,
} from "../../../data/iso27001-controls";

export default function Iso27001Framework() {
  const columns = [
    { accessorKey: "controlId", header: "Control ID", size: 120 },
    { accessorKey: "clause", header: "Clause", size: 220 },
    { accessorKey: "domain", header: "Domain", size: 140 },
    { accessorKey: "controlObjective", header: "Control Objective", size: 220 },
    { accessorKey: "controlStatement", header: "Control Statement", size: 360 },
    { accessorKey: "implementationGuidance", header: "Implementation Guidance", size: 260 },
    { accessorKey: "rbiMapping", header: "RBI Mapping", size: 140 },
    { accessorKey: "sebiMapping", header: "SEBI Mapping", size: 140 },
    { accessorKey: "evidence", header: "Evidence", size: 240 },
    { accessorKey: "auditMethod", header: "Audit Method", size: 200 },
  ];
  return (
    <div>
      <section className="relative overflow-hidden py-16" style={{ background: "var(--gradient-hero)" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="aurora" />
          <div className="noise" />
        </div>

        <div className="container-ivalue relative">
          <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
            International Frameworks / ISO 27001
          </div>

          <h1 className="mt-4 text-balance text-4xl font-extrabold tracking-[-0.03em] text-text-primary md:text-6xl">
            ISO/IEC 27001:2022
            <br />
            Information Security Management
          </h1>

          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-text-secondary">
            The global gold standard for information security management systems, now with enhanced controls for cloud, threat intelligence, and privacy.
          </p>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <a
              href="#iso-catalog"
              className="focus-ring sheen inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-[#10B981] to-[#059669] px-5 text-sm font-semibold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
            >
              Download Annex A Mapping
            </a>
            <a
              href="#india-crossref"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl border border-border-subtle bg-white/5 px-5 text-sm font-semibold text-text-primary transition-colors hover:bg-white/10"
            >
              View Indian Regulation Cross-Reference
            </a>
            <Link
              to="/frameworks/india/rbi"
              className="focus-ring inline-flex h-12 items-center justify-center rounded-xl px-5 text-sm font-semibold text-[color:var(--text-link)] transition-colors hover:text-text-primary"
            >
              Back to India →
            </Link>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { k: "Clauses", v: "10" },
              { k: "Annex A Controls", v: "93" },
              { k: "Control Categories", v: "4" },
            ].map((s) => (
              <div key={s.k} className="glass-card sheen p-5">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {s.k}
                </div>
                <div className="mt-2 font-[family:var(--font-space-grotesk)] text-3xl font-semibold text-text-primary">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20">
        <div className="container-ivalue">
          <ScrollReveal>
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Management system clauses (4–10)
                </div>
                <div className="mt-4 space-y-2">
                  {ISO_CLAUSES.map((c) => (
                    <div key={c} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-2 text-sm text-text-secondary">
                      {c}
                    </div>
                  ))}
                </div>
              </div>

              <div className="glass-card p-6">
                <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">
                  Annex A categories
                </div>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">
                  {ISO_ANNEX_A_CATEGORIES.map((c) => (
                    <div key={c.name} className="rounded-xl border border-border-subtle bg-white/5 px-3 py-3">
                      <div className="text-sm font-semibold text-text-primary">{c.name}</div>
                      <div className="mt-1 text-sm text-text-secondary">{c.count} controls</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <section className="bg-background-primary py-20" id="iso-catalog">
        <div className="container-ivalue">
          <ScrollReveal>
            <div>
              <h2 className="text-3xl font-extrabold tracking-[-0.02em] text-text-primary md:text-5xl">ISO 27001 control catalog</h2>
              <p className="mt-3 max-w-3xl text-lg text-text-secondary">
                Seeded with representative Annex A controls (expandable to the full 93).
              </p>
            </div>
          </ScrollReveal>

          <div className="mt-10">
            <GenericCatalogTable
              data={ISO27001_CATALOG}
              columns={columns}
              csvColumns={[
                { header: "Control ID", accessorKey: "controlId" },
                { header: "Clause", accessorKey: "clause" },
                { header: "Domain", accessorKey: "domain" },
                { header: "Objective", accessorKey: "controlObjective" },
                { header: "Statement", accessorKey: "controlStatement" },
                { header: "Guidance", accessorKey: "implementationGuidance" },
                { header: "RBI", accessorKey: "rbiMapping" },
                { header: "SEBI", accessorKey: "sebiMapping" },
                { header: "Evidence", accessorKey: "evidence" },
                { header: "Audit", accessorKey: "auditMethod" },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-background-secondary py-20" id="india-crossref">
        <div className="container-ivalue">
          <ScrollReveal>
            <CrossFrameworkMapping
              rows={ISO_TO_INDIA_CROSSMAP.map((r) => ({
                controlArea: r.isoControl,
                rbi: r.rbiControl,
                sebi: r.sebiControl,
                irdai: "-",
                certIn: r.certInControl,
              }))}
            />
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
